/**
 * perf-defer.js — tiered third-party script deferral.
 *
 * Must run before any third-party script. Load as the FIRST <script>
 * in <head>, with no async and no defer.
 *
 * Tiers:
 *   'idle'        release on requestIdleCallback with a hard timeout.
 *   'interaction' release on first interaction. Avoid while INP is failing.
 *
 * Debug:  window.PerfDefer.report()   — what is held
 *         window.PerfDefer.release()  — release everything now
 */
(function () {
  'use strict';

  // ─── Config — match on origin, never on a versioned path ─────────────────
  var RULES = [
    { host: 'nitrocommerce.ai', tier: 'idle', timeout: 2500 },
    { host: 'wigzo.com',        tier: 'idle', timeout: 4000 },
    { host: 'wigzopush.com',    tier: 'idle', timeout: 4000 },
    { host: 'clarity.ms',       tier: 'idle', timeout: 4000 },
    { path: 'bitespeed-popups', tier: 'idle', timeout: 4000 },
    { path: 'yt-loyalty-widget', tier: 'idle', timeout: 4000 }
  ];

  // Never delayed, whatever else matches.
  var NEVER = [
    'consentmo', 'pandectes', 'avada-cookie-consent',
    'cdn.shopify.com/s/files'
  ];

  var IDLE_DEFAULT = 2500;
  var INTERACTION_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel', 'scroll'];

  var queue = [];
  var released = {};
  var t0 = Date.now();
  var patched = false;
  var reentry = false;

  function log(name, detail) {
    try {
      document.dispatchEvent(new CustomEvent('perfdefer:' + name, { detail: detail }));
    } catch (e) {}
  }

  function ruleFor(src) {
    if (!src) return null;
    var url;
    try { url = new URL(src, location.href); } catch (e) { return null; }
    var full = url.hostname + url.pathname;
    for (var i = 0; i < NEVER.length; i++) {
      if (full.indexOf(NEVER[i]) !== -1) return null;
    }
     for (var j = 0; j < RULES.length; j++) {
      var r = RULES[j];
      var match = false;

      if (r.host && (url.hostname === r.host || url.hostname.endsWith('.' + r.host))) {
        match = true;
      }
      if (r.path && full.indexOf(r.path) !== -1) {
        match = true;
      }

      if (match) {
        if (released[r.tier]) return null;
        return r;
      }
    }
    return null;
  }

  function hold(el, src, rule) {
    el.type = 'text/perf-deferred';
    el.dataset.perfSrc = src;
    el.removeAttribute('src');
    queue.push({ el: el, src: src, tier: rule.tier, heldAt: Date.now() - t0 });
    log('held', { src: src, tier: rule.tier, at: Date.now() - t0 });
  }

  function patch() {
    if (patched) return;
    patched = true;

    var origCreate = document.createElement;
    document.createElement = function (tag) {
      var el = origCreate.apply(document, arguments);
      if (String(tag).toLowerCase() !== 'script') return el;
      var desc = Object.getOwnPropertyDescriptor(HTMLScriptElement.prototype, 'src');
      try {
        Object.defineProperty(el, 'src', {
          configurable: true,
          get: function () { return el.dataset.perfSrc || desc.get.call(this); },
          set: function (v) {
            if (reentry) return desc.set.call(this, v);
            var rule = ruleFor(v);
            if (!rule) return desc.set.call(this, v);
            reentry = true;
            try { hold(this, v, rule); } finally { reentry = false; }
          }
        });
      } catch (e) {}
      return el;
    };

    if ('MutationObserver' in window) {
      new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          var added = records[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (!n || n.nodeName !== 'SCRIPT' || n.type === 'text/perf-deferred') continue;
            var src = n.getAttribute && n.getAttribute('src');
            var rule = ruleFor(src);
            if (!rule) continue;
            n.addEventListener('beforescriptexecute', function (ev) { ev.preventDefault(); });
            hold(n, src, rule);
          }
        }
      }).observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  // rAF is PAUSED in hidden/background tabs. Never schedule release work on it
  // unconditionally, or a background-tab load silently never gets its scripts.
  function schedule(fn) {
    if (document.visibilityState === 'hidden' || !('requestAnimationFrame' in window)) {
      return setTimeout(fn, 0);
    }
    return requestAnimationFrame(fn);
  }

  function releaseTier(tier) {
    if (released[tier]) return;
    released[tier] = true;

    var batch = queue.filter(function (q) { return q.tier === tier; });
    queue = queue.filter(function (q) { return q.tier !== tier; });
    if (!batch.length) return;

    log('released', { tier: tier, count: batch.length, at: Date.now() - t0 });

    (function next(i) {
      if (i >= batch.length) return;
      schedule(function () {
        var item = batch[i];
        var s = document.createElement('script');
        var attrs = item.el.attributes || [];
        for (var a = 0; a < attrs.length; a++) {
          if (attrs[a].name === 'type' || attrs[a].name === 'src') continue;
          s.setAttribute(attrs[a].name, attrs[a].value);
        }
        s.async = false;
        var done = function () { next(i + 1); };
        s.addEventListener('load', done);
        s.addEventListener('error', done);
        reentry = true;
        try { s.src = item.src; } finally { reentry = false; }
        (item.el.parentNode || document.head).appendChild(s);
        if (item.el.parentNode) item.el.parentNode.removeChild(item.el);
      });
    })(0);
  }

  function releaseAll() {
    releaseTier('idle');
    releaseTier('interaction');
  }

  function armIdle() {
    var fired = false;
    var go = function () { if (!fired) { fired = true; releaseTier('idle'); } };
    var timeout = IDLE_DEFAULT;
    RULES.forEach(function (r) {
      if (r.tier === 'idle' && r.timeout) timeout = Math.min(timeout, r.timeout);
    });
    if ('requestIdleCallback' in window) {
      requestIdleCallback(go, { timeout: timeout });
    } else {
      setTimeout(go, timeout);
    }
    setTimeout(go, timeout + 500);
  }

  function armInteraction() {
    var fired = false;
    var go = function () {
      if (fired) return;
      fired = true;
      releaseTier('interaction');
    };
    INTERACTION_EVENTS.forEach(function (e) {
      window.addEventListener(e, go, { passive: true, capture: true, once: true });
    });
    var cap = 0;
    RULES.forEach(function (r) {
      if (r.tier === 'interaction' && r.timeout) cap = Math.max(cap, r.timeout);
    });
    if (cap) setTimeout(go, cap);
  }

  var armedOnce = false;
  function arm() {
    if (armedOnce) return;
    armedOnce = true;
    armIdle();
    armInteraction();
  }

  try {
    patch();
    if (document.readyState === 'complete') {
      arm();
    } else {
      window.addEventListener('load', arm, { once: true });
      // A stalled load must not hold scripts forever.
      setTimeout(arm, 6000);
      // A tab that loaded in the background releases as soon as it is looked at.
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'visible') arm();
      });
    }
    // Absolute backstop. Nothing is ever held past this, whatever else fails.
    setTimeout(releaseAll, 12000);
  } catch (e) {
    releaseAll();
  }

  window.PerfDefer = {
    release: releaseAll,
    releaseTier: releaseTier,
    report: function () {
      return queue.map(function (q) {
        return { src: q.src, tier: q.tier, heldForMs: Date.now() - t0 - q.heldAt };
      });
    }
  };
})();